import type { FlowCardsResponse, FlowCommentsResponse, FlowVersionsResponse } from "@/lib/pb-types";

/**
 * Flows: agents hand Pipe finished work to read and decide on, one card per
 * task, and Pipe hands agents work as queued cards whose v1 is the brief. The lifecycle itself lives in `pb_hooks/flows-lib.js`; this file holds
 * what the screen derives from it, plus the sandbox document for live components.
 */

export type FlowBlock =
  | { type: "markdown"; text: string }
  | { type: "image" | "video"; asset?: string; filename?: string; url?: string; caption?: string }
  | { type: "html"; html: string; title?: string };

export type FlowAd = {
  product: string;
  audience: string;
  hook: string;
  caption: string;
  headline: string;
  reasoning: string;
  creative: FlowBlock[];
};

export type FlowCitation = { text: string; source: string; version?: number; note?: string };

/**
 * A citation while Pipe writes his decision. `parked` (never sent) tucks one away into the
 * stack above the composer once he is done with it; only the open one shows its editor.
 */
export type DraftCitation = FlowCitation & { parked?: boolean };

/** A new citation becomes the open one; every other is parked. A passage already cited reopens. */
export function openNewCitation(list: readonly DraftCitation[], citation: FlowCitation): DraftCitation[] {
  if (list.some((item) => item.text === citation.text)) return reopenCitation(list, citation.text);
  return [...list.map((item) => ({ ...item, parked: true })), { ...citation, parked: false }];
}

/** Opens one citation for editing and parks the rest. */
export function reopenCitation(list: readonly DraftCitation[], text: string): DraftCitation[] {
  return list.map((item) => ({ ...item, parked: item.text !== text }));
}

export function parkCitation(list: readonly DraftCitation[], text: string): DraftCitation[] {
  return list.map((item) => (item.text === text ? { ...item, parked: true } : item));
}

/** What goes to the server: the citations without the composer's own state. */
export function sentCitations(list: readonly DraftCitation[]): FlowCitation[] {
  return list.map(({ parked: _parked, ...citation }) => citation);
}

/** 12.43 → "0:12.4", 3723.5 → "1:02:03.5": a point in a video to a tenth of a second. */
export function formatVideoTime(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds * 10) / 10 : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = (total % 60).toFixed(1).padStart(4, "0");
  return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${rest}` : `${minutes}:${rest}`;
}

/** The citation for one moment of a video: its name and the time, for the agent to find the frame. */
export function videoCitation(name: string, seconds: number, version?: number): FlowCitation {
  return { text: `${name || "วิดีโอ"} @ ${formatVideoTime(seconds)}`, source: "วิดีโอ", version };
}

export type FlowCard = FlowCardsResponse;
export type FlowVersion = FlowVersionsResponse<FlowAd, FlowBlock[]>;
export type FlowComment = FlowCommentsResponse<FlowCitation[]>;
/** What Pipe can pick in the decision bar. */
export type FlowDecision = "approve" | "changes" | "reject";
/** What a card or comment can carry: the three above, or Pipe's answer to an agent's question. */
export type FlowOutcome = FlowDecision | "answer";

/** รอผม first: it is what Pipe opens Flows to check, and the default tab. */
export const FLOW_TABS = ["review", "queue", "agent", "done"] as const;
export type FlowTab = (typeof FLOW_TABS)[number];

export const TAB_LABEL: Record<FlowTab, string> = { queue: "คิว agent", agent: "รอ agent", review: "รอผม", done: "เสร็จ" };
/** What an empty tab says. */
export const EMPTY_LABEL: Record<FlowTab, string> = {
  queue: "ยังไม่มีงานรอ agent หยิบ · ส่งจาก Tasks หรือ Inbox ด้วยปุ่ม ส่งให้ agent",
  agent: "ยังไม่มีการ์ดในแท็บนี้",
  review: "ไม่มีการ์ดรอคุณอยู่",
  done: "ยังไม่มีการ์ดในแท็บนี้",
};
export const DECISION_LABEL: Record<FlowDecision, string> = {
  approve: "Approve",
  changes: "Request changes",
  reject: "Reject",
};
export const OUTCOME_LABEL: Record<FlowOutcome, string> = { ...DECISION_LABEL, answer: "Answered" };

/**
 * Where an approval sends the work, for the Approve button: a card that came from a queue
 * fills its item there (drafts-lib.js), a queued Task is completed. Null when it goes nowhere.
 */
export function approveTarget(card: Pick<FlowCard, "origin" | "source_kind">): { label: string; detail: string } | null {
  if (card.origin !== "owner") return null;
  switch (card.source_kind) {
    case "organic":
      return { label: "Organic Queue", detail: "วิดีโอและแคปชันจะเติมลงชิ้นงานเดิมใน Organic Queue แล้วกดเลื่อนเป็น \"พร้อมลง\" ที่นั่น" };
    case "ad":
      return { label: "Ads Queue", detail: "วิดีโอ แคปชัน และหัวข้อจะเติมลงร่างแอดเดิมใน Ads Queue แล้วกด \"ย้ายเข้าคิว\" ที่นั่น" };
    case "task":
      return { label: "ปิด Task", detail: "Task ที่ส่งงานนี้มาจะถูกติ๊กว่าเสร็จ" };
    default:
      return null;
  }
}

export function tabOf(card: Pick<FlowCard, "status">): FlowTab {
  switch (card.status) {
    case "queued": return "queue";
    case "review": return "review";
    case "done": return "done";
    default: return "agent";
  }
}

/** Oldest first while it waits on someone, so nothing sinks; newest first once it has moved on. */
export function sortForTab(cards: readonly FlowCard[], tab: FlowTab): FlowCard[] {
  const sorted = [...cards];
  const oldestFirst = tab === "review" || tab === "queue";
  sorted.sort((a, b) => (oldestFirst ? a.created.localeCompare(b.created) : b.updated.localeCompare(a.updated)));
  return sorted;
}

/** The deep link that opens one card on its own tab, which is the only place Flows shows it. */
export function cardLink(card: Pick<FlowCard, "id" | "status">): string {
  return `/flows?tab=${tabOf(card)}&card=${encodeURIComponent(card.id)}`;
}

/** Where to land after deciding: the card that took its place, else the one before it. */
export function nextAfter(cards: readonly FlowCard[], id: string): string | null {
  const index = cards.findIndex((card) => card.id === id);
  if (index < 0) return cards[0]?.id ?? null;
  return cards[index + 1]?.id ?? cards[index - 1]?.id ?? null;
}

/** A reason can sit in the overall comment or on any one citation. */
export function canSubmit(decision: FlowDecision | null, comment: string, citations: readonly FlowCitation[] = []): boolean {
  if (!decision) return false;
  return decision === "approve" || comment.trim().length > 0 || citations.some((citation) => citation.note?.trim());
}

/** What Pipe pastes into a chat so an agent takes this card. Queued work is a brief, not a thread to answer. */
export function pickCommand(card: Pick<FlowCard, "id" | "title" | "status">): string {
  if (card.status === "queued") {
    return `ใช้ kobo_flow_pick หยิบการ์ด Flows id ${card.id} ("${card.title}") อ่าน brief (v1) กับไฟล์แนบ แล้วทำตาม kobo_flows_instructions`;
  }
  return `ใช้ kobo_flow_pick หยิบการ์ด Flows id ${card.id} ("${card.title}") อ่าน thread กับ citation ทั้งหมด แล้วทำตาม kobo_flows_instructions`;
}

/** Everything a card shows as text, ad fields first, in reading order. */
export function adFields(ad: FlowAd): { label: string; value: string }[] {
  return [
    { label: "สินค้า", value: ad.product },
    { label: "กลุ่มเป้าหมาย", value: ad.audience },
    { label: "Hook", value: ad.hook },
    { label: "Caption", value: ad.caption },
    { label: "Headline", value: ad.headline },
    { label: "เหตุผล", value: ad.reasoning },
  ];
}

/**
 * The window message of the frame's helper script. Any script in the frame can
 * send it too, so what it says is never trusted beyond the frame's own box.
 */
export const FRAME_MESSAGE = "busee-flow-frame";

export type FrameMessage =
  | { source: typeof FRAME_MESSAGE; kind: "height"; height: number }
  | { source: typeof FRAME_MESSAGE; kind: "escape" }
  | { source: typeof FRAME_MESSAGE; kind: "selection"; text: string; rect: { left: number; top: number; bottom: number } | null };

export function readFrameMessage(data: unknown): FrameMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const message = data as Partial<FrameMessage>;
  if (message.source !== FRAME_MESSAGE) return null;
  if (message.kind === "height" && Number.isFinite(message.height)) return message as FrameMessage;
  if (message.kind === "escape") return message as FrameMessage;
  if (message.kind === "selection" && typeof message.text === "string") {
    const rect = message.rect;
    if (rect !== null && ![rect?.left, rect?.top, rect?.bottom].every(Number.isFinite)) return null;
    return message as FrameMessage;
  }
  return null;
}

// Measures the body, not the document: the document is never shorter than the
// frame, so a frame could only ever grow. The body's scroll height counts what overflows it
// (a tall SVG or grid), and `flow-root` keeps floats and margins inside it, so the frame is
// never a few pixels short. The frame itself never scrolls (a nested scroller rubber-bands on
// a Mac and traps the wheel) unless its height was capped and content really is hidden; the
// check waits until the parent has resized the frame. Reports that height, and its text selection,
// so a passage can be cited: the parent has no other way to see into a frame
// without same-origin rights.
const FRAME_HELPER = `(function(){
var post=function(m){m.source=${JSON.stringify(FRAME_MESSAGE)};parent.postMessage(m,"*")};
var fitTimer,fit=function(){var d=document.documentElement;d.style.overflowY=d.scrollHeight>innerHeight+2?"auto":"hidden"};
var size=function(){var b=document.body;if(!b)return;post({kind:"height",height:Math.max(b.getBoundingClientRect().height,b.scrollHeight)});clearTimeout(fitTimer);fitTimer=setTimeout(fit,300)};
var timer;
document.addEventListener("selectionchange",function(){clearTimeout(timer);timer=setTimeout(function(){
var s=getSelection(),t=s?String(s).trim():"",r=null;
if(t&&s.rangeCount){var b=s.getRangeAt(0).getBoundingClientRect();r={left:b.left,top:b.top,bottom:b.bottom}}
post({kind:"selection",text:t,rect:r})},120)});
addEventListener("load",size);
addEventListener("resize",fit);
document.addEventListener("load",size,true);
document.addEventListener("keydown",function(e){if(e.key==="Escape")post({kind:"escape"})});
if(window.ResizeObserver){addEventListener("DOMContentLoaded",function(){new ResizeObserver(size).observe(document.body)})}
})();`;

// The frame has an opaque origin (no `allow-same-origin`), so agent HTML cannot
// read Kobo or its session whatever this says. The CSP only stops `fetch` and
// forms: loading from https stays open for chart libraries and images, which
// also means a component could beacon its own content or navigate itself.
// Components come from Pipe's own agents, so that is accepted, not prevented.
const FRAME_CSP =
  "default-src 'none'; script-src 'unsafe-inline' https:; style-src 'unsafe-inline' https:; img-src data: blob: https:; media-src data: blob: https:; font-src data: https:; form-action 'none'";

function escapeAttribute(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

export function buildFrameDocument(html: string): string {
  return [
    "<!doctype html><html><head>",
    `<meta http-equiv="Content-Security-Policy" content="${escapeAttribute(FRAME_CSP)}">`,
    "<style>html,body{margin:0}html{overflow-y:hidden}body{display:flow-root;padding:12px;font-family:sans-serif;background:#fff;color:#111}</style>",
    `<script>${FRAME_HELPER}</script>`,
    "</head><body>",
    html,
    "</body></html>",
  ].join("");
}

/** The cards either side of `id` in the list on screen, for ← → in an open card. */
export function neighbours<T extends { id: string }>(listed: readonly T[], id: string): { prev: T | null; next: T | null; index: number } {
  const index = listed.findIndex((card) => card.id === id);
  if (index < 0) return { prev: null, next: null, index };
  return { prev: listed[index - 1] ?? null, next: listed[index + 1] ?? null, index };
}

/**
 * Whether a keypress belongs to whatever has focus rather than to card paging: typing,
 * a select, a slider or a video's own controls keep their arrow keys.
 */
export function keyBelongsToField(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).closest !== "function") return false;
  const element = target as HTMLElement;
  if (element.isContentEditable) return true;
  return Boolean(element.closest("input, textarea, select, video, audio, [role='slider'], [data-video-player], [contenteditable='true'], .kobo-video"));
}

/** Machines that pick a Flows category by itself (Workers: categories), matched on the category key. */
export function workersFor<W extends { categories?: unknown }>(categoryKey: string, workers: readonly W[]): W[] {
  const keyOf = (name: string) => name.trim().toLowerCase().replace(/\s+/g, "-");
  return workers.filter((worker) => Array.isArray(worker.categories) && worker.categories.some((name) => typeof name === "string" && keyOf(name) === categoryKey));
}


/**
 * Whether an Approve carries something to do: comment text or a cited passage with a note.
 * The server then leaves handed-out work for an agent instead of closing it (flows-lib.js decide).
 */
export function approvalHasComment(comment: string, citations: readonly { note?: string }[]): boolean {
  return Boolean(comment.trim()) || citations.some((citation) => Boolean(citation.note?.trim()));
}

/** Handed-out work Pipe approved with a comment: an agent carries the comment out, then it closes. */
export function awaitsCommentWork(card: Pick<FlowCard, "origin" | "decision" | "status">): boolean {
  return card.origin === "owner" && card.decision === "approve" && (card.status === "decided" || card.status === "picked");
}
